import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { WebPageSchema } from '@/components/feature/StructuredData';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function ComicCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 韩国漫画网免费在线阅读`}
          title={`${comic.title} - 韩漫免费阅读`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <p className="text-white text-xs line-clamp-2">{comic.description}</p>
        </div>
        {comic.isNew && (
          <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs font-medium px-2 py-0.5 rounded-full">
            NEW
          </span>
        )}
        {comic.isHot && (
          <span className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-medium px-2 py-0.5 rounded-full">
            HOT
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent h-16"></div>
        <span className="absolute bottom-2 right-2 text-white/80 text-xs">{comic.status}</span>
      </div>
      <div className="mt-2 px-0.5">
        <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-foreground-500">{comic.author}</span>
          <span className="text-xs text-foreground-400">|</span>
          <span className="text-xs text-accent-500 font-medium">{comic.rating}分</span>
        </div>
      </div>
    </Link>
  );
}

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics = useMemo(() => {
    let result = slug === 'all' ? [...comics] : comics.filter((c) => c.categorySlug === slug);

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
    }

    return result;
  }, [slug, sortBy]);

  const categoryTitle = slug === 'all' ? '全部漫画' : currentCategory.name;
  const categoryDesc = slug === 'all'
    ? '浏览韩国漫画网全部免费韩漫作品，覆盖恋爱、奇幻、动作、悬疑等所有类型。'
    : `浏览${currentCategory.name}类韩漫作品，韩国漫画网为您精选${currentCategory.name}类免费韩漫在线阅读全集。`;
  const categoryKeywords = slug === 'all'
    ? '最新韩漫,韩漫免费阅读,韩漫在线阅读全集,韩国漫画网'
    : `${currentCategory.name}韩漫,${currentCategory.name}漫画,韩漫免费阅读,韩国漫画网`;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryTitle },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${categoryTitle} - 韩漫免费阅读 - 韩国漫画网`}
        description={categoryDesc}
        keywords={categoryKeywords}
        canonicalUrl={`https://www.koreacomic.cn/category/${slug}`}
      />
      <WebPageSchema
        title={`${categoryTitle} - 韩国漫画网`}
        description={categoryDesc}
        url={`https://www.koreacomic.cn/category/${slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-4 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {categoryTitle}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {filteredComics.length} 部作品
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort Bar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">排序：</span>
                {[
                  { key: 'latest' as const, label: '最新更新' },
                  { key: 'popular' as const, label: '人气最高' },
                  { key: 'rating' as const, label: '评分最高' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Comic Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>

            {filteredComics.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                <p className="text-foreground-500 mt-3">该分类下暂无漫画作品</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}